import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Container,
  DinamicContainer,
  Dropdown,
  Operator,
  OperatorContainer,
  OptionsContainer,
  Text,
} from './styles';

import {
  OPERATIONS,
  buildExpressions,
} from '../../helper';

/** dropdown component */
const CustomDropdown = ({
  flex, onChange, options, value, disabled,
}) => (
  <Dropdown
    flex={flex}
    selection
    scrolling
    onChange={(e, obj) => onChange(obj.value)}
    options={options}
    value={value}
    disabled={disabled}
  />
);

const SkipCondition = (props) => {
  /** props */
  const {
    data,
    isFirst,
    onCreate,
    onDelete,
    onUpdate,
    questions,
  } = props;

  const {
    questionId: question,
    questionOptionId: answer,
    operation,
  } = data;

  /** states */
  const [expressions, setExpressions] = useState([]);
  const [selectedExpression, setSelectedExpression] = useState();
  const [answers, setAnswers] = useState([]);
  // it defines if expressions and answers dropdown should be reverted
  const [revert, setRevert] = useState(false);

  const handleUpdateAnswer = (value) => {
    onUpdate({ questionOptionId: value });
  };

  const handleUpdateQuestion = (value) => {
    const questionObject = _.find(questions, { key: value });

    if (_.isEmpty(questionObject)) return;

    const builtExpressions = buildExpressions(questionObject.type);
    const expressionObject = builtExpressions[operation || selectedExpression] || _.first(builtExpressions);
    const newAnswers = questionObject.answers;

    setSelectedExpression(expressionObject.value);
    setRevert(questionObject.type === 'multiple_select');
    setExpressions(builtExpressions);
    setAnswers(newAnswers);

    const skipLogicObject = {
      questionId: value,
      questionOptionId: !_.isEmpty(newAnswers) ? _.first(newAnswers).value : '',
      operation: expressionObject.value,
    };

    onUpdate(skipLogicObject);
  };

  const handleUpdateExpression = (value) => {
    setSelectedExpression(value);
    onUpdate({ operation: value });
  };

  /** life cycle */
  useEffect(() => {
    /** initial selected answer */
    if (!question || !_.isEmpty(answers)) return;

    const { answers: answersList = [] } = _.find(questions, { key: question });
    setAnswers(answersList);
  }, [answers, question, questions]);

  useEffect(() => {
    /** initial selected expression */
    if (!question || !_.isEmpty(expressions) || selectedExpression) return;

    const { type } = _.find(questions, { key: question });
    const builtExpressions = buildExpressions(type);

    setExpressions(builtExpressions);
    setSelectedExpression(operation || _.first(builtExpressions).value);
  }, [answers, expressions, operation, question, questions, selectedExpression]);

  useEffect(() => {
    /** set true if dropdowns must revert */
    if (!question) return;

    const questionObject = _.find(questions, { key: question });
    setRevert(questionObject.type === 'multiple_select');
  }, [question, questions]);

  return (
    <Container>
      <Text>{isFirst ? OPERATIONS.IF : OPERATIONS.AND}</Text>

      <OptionsContainer>
        {/* Questions dropdown */}
        <CustomDropdown
          flex={5}
          onChange={handleUpdateQuestion}
          options={questions}
          value={question}
        />

        <DinamicContainer revert={revert}>
          {/* Is/Is not OR Is selected/Is not selected dropdown */}
          <CustomDropdown
            flex={revert ? 3 : 1}
            onChange={handleUpdateExpression}
            options={expressions}
            value={selectedExpression}
            disabled={!question}
          />

          {/*  Answers dropdown */}
          <CustomDropdown
            flex={revert ? 1 : 3}
            onChange={handleUpdateAnswer}
            options={answers}
            value={answer}
            disabled={!question || !!_.find(answers, { text: '' })}
          />
        </DinamicContainer>
      </OptionsContainer>

      <OperatorContainer>
        <Operator onClick={onCreate}>
          +
        </Operator>

        {!isFirst && (
          <Operator onClick={onDelete}>
            -
          </Operator>
        )}
      </OperatorContainer>
    </Container>
  );
};

SkipCondition.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      answers: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }).isRequired,
      ),
    }).isRequired,
  ).isRequired,
  isFirst: PropTypes.bool,
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    questionId: PropTypes.string,
    questionOptionId: PropTypes.string,
    operation: PropTypes.string,
  }).isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

SkipCondition.defaultProps = {
  isFirst: false,
};

CustomDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  flex: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

CustomDropdown.defaultProps = {
  disabled: false,
};

export default SkipCondition;
